import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

interface NumberInputProps {
  defaultValue?: number | null;
  onValueChange: (value: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  format?: "currency" | "number";
  suffix?: string;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  defaultValue = null,
  onValueChange,
  placeholder = "Voer nummer in...",
  min,
  max,
  format = "number",
  suffix,
}) => {
  // Store the raw numeric value (no formatting)
  const [rawValue, setRawValue] = useState<string>(
    defaultValue !== null && defaultValue !== undefined
      ? String(defaultValue)
      : "",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRawValue(
      defaultValue !== null && defaultValue !== undefined
        ? String(defaultValue)
        : "",
    );
    setError(null);
  }, [defaultValue]);

  // Format number with thousands separators for display
  const formatWithSeparators = (numStr: string): string => {
    if (!numStr || numStr === "" || numStr === "-") return numStr;

    // Remove any existing separators
    const cleaned = numStr.replace(/\./g, "");

    // Parse to number
    const num = parseFloat(cleaned);
    if (isNaN(num)) return numStr;

    // Format with thousands separator (.) but no currency symbol
    return new Intl.NumberFormat("nl-BE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(num);
  };

  // Get display value with proper formatting
  const getDisplayValue = (): string => {
    if (!rawValue || rawValue === "" || rawValue === "-") {
      return rawValue;
    }

    if (format === "currency") {
      // Always show formatted with separators
      return formatWithSeparators(rawValue);
    }

    return rawValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Remove currency symbols and spaces that might be pasted
    newValue = newValue.replace(/[€\s]/g, "");

    // Remove existing separators (dots)
    newValue = newValue.replace(/\./g, "");

    // Update raw value
    setRawValue(newValue);
    setError(null);

    // Allow empty or just minus sign (for negative numbers in progress)
    if (newValue === "" || newValue === "-") {
      onValueChange(null);
      return;
    }

    // Parse and validate
    const numValue = parseFloat(newValue);

    // Check if it's a valid number
    if (isNaN(numValue)) {
      setError("Voer een geldig nummer in");
      toast.error("Voer een geldig nummer in (geen tekst)");
      onValueChange(null);
      return;
    }

    // Apply min/max validation if provided
    if (min !== undefined && numValue < min) {
      setError(`Waarde moet minimaal ${min} zijn`);
      toast.error(`Waarde moet minimaal ${min} zijn`);
      onValueChange(null);
      return;
    }

    if (max !== undefined && numValue > max) {
      setError(`Waarde mag maximaal ${max} zijn`);
      toast.error(`Waarde mag maximaal ${max} zijn`);
      onValueChange(null);
      return;
    }

    // Valid number - send clean number to backend
    onValueChange(numValue);
  };

  return (
    <div className="p-4" onClick={(e) => e.stopPropagation()}>
      <div className="relative">
        {format === "currency" && rawValue && (
          <span className="text-neutral-primary pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
            €
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={getDisplayValue()}
          onChange={handleChange}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder={placeholder}
          required
          title={`Voer een geldig nummer in${min !== undefined && max !== undefined ? ` tussen ${min} en ${max}` : ""}`}
          className={`w-full rounded-md border px-3 py-2 transition-colors focus:ring-2 focus:outline-none ${
            format === "currency" && rawValue ? "pl-8" : ""
          } ${
            suffix ? "pr-12" : ""
          } ${
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-neutral-primary/20 focus:ring-moss-primary focus:border-transparent"
          }`}
          autoFocus
        />
        {suffix && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
            {suffix}
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && min !== undefined && max !== undefined && (
        <p className="text-neutral-secondaryMid mt-1 text-xs">
          Bereik: {min} - {max}
        </p>
      )}
      {!error && format === "currency" && (
        <p className="text-neutral-secondary-mid mt-1 text-xs">
          💡 Bedragen worden automatisch geformatteerd (bijv. € 250.000)
        </p>
      )}
    </div>
  );
};
